<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  GrammarCheckbox,
  GrammarFooter,
  GrammarLocalLoader,
  GrammarModal,
  GrammarPrimaryButton,
  GrammarRadioButtonGroup,
  GrammarRadioButtonItem,
  GrammarSecondaryButton,
  GrammarSectionHeader,
  GrammarTextField,
} from '@nora-monorepo/grammar-ui';
import type {
  ConsentScopePayload,
} from '@nora-monorepo/nora-api-client';
import {
  ConsentScopeType,
  ConsentStatusEnum,
} from '@nora-monorepo/nora-api-client';
import {
  CustomDurationModal,
  textQuaternary,
  useApiStore,
  useToastStore,
} from '@nora-monorepo/ui-shared';

import { getConsentInvitedByUser, getConsentTitle } from '#nora-ui/components/consent/consent-utils';
import { useConsentModalStore } from '#nora-ui/components/modals/consent-modal/consent-modal-store';
import { useConsentStore } from '#nora-ui/stores/consent-store';
import { useOperationStore } from '#nora-ui/stores/operation-store';

const { t } = useI18n();
const consentStore = useConsentStore();
const consentModalStore = useConsentModalStore();
const api = useApiStore();
const toastStore = useToastStore();
const operationStore = useOperationStore();

const { currentConsent, isShowingConsentModal, isError }
  = storeToRefs(consentModalStore);

const showCustomDurationModal = ref(false);

const expirationDate = ref<Date | null>(null);

enum ExpirationOption {
  OneDay = 'one_day',
  ThreeDays = 'three_days',
  FiveDays = 'five_days',
  Custom = 'custom',
}

const activeExpirationButton = ref<ExpirationOption | null>(null);

const isAlwaysAcceptChecked = ref(false);
const isShareLocationChecked = ref(true);
const isExpirationChecked = ref(false);
const acceptOperationInvitation = ref(false);
const isAccepting = ref(false);

function handleAlwaysAcceptEvent(evt: boolean) {
  isAlwaysAcceptChecked.value = evt;
}

function handleShareLocationEvent(evt: boolean) {
  isShareLocationChecked.value = evt;
}

let resolveExpirationPromise:
  | {
    resolve: (date: Date) => void;
    reject: () => void;
  }
  | undefined;

function handleExpirationDateEvent(evt: boolean) {
  isExpirationChecked.value = evt;

  if (isExpirationChecked.value === false) {
    activeExpirationButton.value = null;
    resolveExpirationPromise?.reject();
  }
}

function onExpirationDateConfirmed(date: Date) {
  if (resolveExpirationPromise) {
    resolveExpirationPromise.resolve(date);
  }

  showCustomDurationModal.value = false;
  resolveExpirationPromise = undefined;
}

function onExpirationDateCancel() {
  if (resolveExpirationPromise) {
    resolveExpirationPromise.reject();
  }

  showCustomDurationModal.value = false;
  resolveExpirationPromise = undefined;
}

async function setExpirationOption(expirationOption: ExpirationOption) {
  activeExpirationButton.value = expirationOption;
  const currentDate = new Date();
  if (expirationOption === ExpirationOption.Custom) {
    try {
      expirationDate.value = await askCustomDuration();
    }
    catch {
      // User cancelled
    }
  }
  else if (expirationOption === ExpirationOption.OneDay) {
    expirationDate.value = new Date(
      currentDate.setDate(currentDate.getDate() + 1),
    );
  }
  else if (expirationOption === ExpirationOption.ThreeDays) {
    expirationDate.value = new Date(
      currentDate.setDate(currentDate.getDate() + 3),
    );
  }
  else if (expirationOption === ExpirationOption.FiveDays) {
    expirationDate.value = new Date(
      currentDate.setDate(currentDate.getDate() + 5),
    );
  }
}

function askCustomDuration(): Promise<Date> {
  return new Promise((resolve, reject) => {
    showCustomDurationModal.value = true;
    resolveExpirationPromise = { resolve, reject };
  });
}

const title = computed(() => {
  return getConsentTitle(currentConsent.value);
});

const invitedByUserName = computed(() => {
  return getConsentInvitedByUser(currentConsent.value);
});

function resetModal() {
  isAlwaysAcceptChecked.value = false;
  isExpirationChecked.value = false;
  isShareLocationChecked.value = true;
  acceptOperationInvitation.value = false;
  activeExpirationButton.value = null;
  expirationDate.value = null;
}

async function handleConfirm() {
  if (isAccepting.value) {
    return;
  }

  acceptOperationInvitation.value = true;

  if (!currentConsent || !currentConsent.value) {
    return;
  }

  isAccepting.value = true;

  try {
    const scopes: ConsentScopePayload[] = [];

    if (isShareLocationChecked.value === true) {
      scopes.push({
        scope: ConsentScopeType.LOCATION,
        expirationDate: expirationDate.value,
      });
    }

    await api.consent.acceptConsent({
      consentId: currentConsent.value.id,
      consentAcceptPayload: {
        scopes,
        alwaysAccept: isAlwaysAcceptChecked.value === true,
      },
    });

    consentModalStore.consentActionDone = currentConsent.value.id;

    await Promise.all([
      consentStore.loadConsents(),
      operationStore.loadOperation(currentConsent.value.operationId),
    ]);
    resetModal();
    consentModalStore.closeModal();
    toastStore.addSuccess({
      message: t('invitations.success'),
    });
  }
  catch {
    toastStore.addError({
      message: t('invitations.errorJoiningOperation'),
    });
  }
  finally {
    isAccepting.value = false;
  }
}

async function handleReject() {
  if (!currentConsent.value) {
    return;
  }
  await consentStore.rejectConsent(currentConsent.value?.id);
  handleCancel();
}

function handleCancel() {
  resetModal();
  consentModalStore.closeModal();
}

onUnmounted(() => {
  resetModal();
});
</script>

<template>
  <div
    class="flex"
    :class="[textQuaternary]"
  >
    <GrammarModal
      :prevent-close-on-click-outside="isAccepting"
      :is-open="isShowingConsentModal"
      :show-close-button="!isAccepting"
      :label="
        isError
          ? t(`consent.loadError`)
          : t(`consent.requestToJoinOperationTitle`, [title])
      "
      @close="handleCancel"
    >
      <template #content>
        <GrammarLocalLoader
          v-if="isAccepting"
          text="Accepting invitation"
        />
        <div
          v-else-if="!isError"
          class="flex flex-col gap-4 mb-4"
        >
          <GrammarTextField
            :read-only="true"
            label="Invited by"
            :value="invitedByUserName ?? ''"
          />
          <div class="flex flex-col">
            <GrammarCheckbox
              :checked="isAlwaysAcceptChecked"
              label="Always accept requests from this user"
              @change="handleAlwaysAcceptEvent"
            />
            <GrammarSectionHeader
              header-type="form-header"
              label="Invitation settings"
            />
            <GrammarCheckbox
              :checked="isShareLocationChecked"
              label="Share location"
              @change="handleShareLocationEvent"
            />
            <GrammarCheckbox
              label="Set expiration time"
              :checked="isExpirationChecked"
              :disabled="!isShareLocationChecked"
              @change="handleExpirationDateEvent"
            />
            <div
              v-if="isExpirationChecked"
              class="flex"
            >
              <GrammarRadioButtonGroup
                :selected-value="activeExpirationButton ?? ''"
              >
                <GrammarRadioButtonItem
                  label="1 day"
                  position="left"
                  :value="ExpirationOption.OneDay"
                  @click="() => setExpirationOption(ExpirationOption.OneDay)"
                />
                <GrammarRadioButtonItem
                  label="3 days"
                  :value="ExpirationOption.ThreeDays"
                  @click="() => setExpirationOption(ExpirationOption.ThreeDays)"
                />
                <GrammarRadioButtonItem
                  label="5 days"
                  :value="ExpirationOption.FiveDays"
                  @click="() => setExpirationOption(ExpirationOption.FiveDays)"
                />
                <GrammarRadioButtonItem
                  label="Custom"
                  position="right"
                  :value="ExpirationOption.Custom"
                  @click="() => setExpirationOption(ExpirationOption.Custom)"
                />
              </GrammarRadioButtonGroup>
            </div>
            <GrammarTextField
              v-if="
                expirationDate
                  && activeExpirationButton === ExpirationOption.Custom
              "
              class="mt-3"
              :read-only="true"
              label="Expiration date"
              :value="expirationDate.toLocaleString()"
            />
          </div>
        </div>
      </template>

      <template #footer>
        <GrammarFooter
          v-if="currentConsent?.status !== ConsentStatusEnum.REJECTED"
        >
          <template #left-button>
            <GrammarSecondaryButton
              :label="t(`consent.status.short.decline`)"
              :disabled="isAccepting"
              @click="handleReject"
            />
          </template>
          <template #right-button>
            <GrammarPrimaryButton
              :label="t(`consent.confirmButton`)"
              :disabled="isAccepting"
              @click="handleConfirm"
            />
          </template>
        </GrammarFooter>
      </template>
    </GrammarModal>

    <CustomDurationModal
      v-model:modal-active="showCustomDurationModal"
      header-title="Custom location expiration"
      :on-confirm="onExpirationDateConfirmed"
      @cancel="onExpirationDateCancel"
    />
  </div>
</template>
